/**
 * Same-Day Shipping Countdown
 * Shows countdown for orders placed before cutoff time on weekdays
 * All times are in German timezone (Europe/Berlin) since warehouse is in Germany
 */

(function () {
  'use strict';

  // German timezone for all calculations (warehouse location)
  const TIMEZONE = 'Europe/Berlin';

  // German weekday names (index 0 = Sunday, 1 = Monday, etc.)
  const WEEKDAYS_DE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

  // German month names for date formatting
  const MONTHS_DE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  /**
   * Get current date/time in German timezone
   * Uses Intl.DateTimeFormat.formatToParts for reliable timezone conversion
   * without relying on locale string parsing
   */
  function getGermanDate() {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: TIMEZONE,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false
    });

    const parts = formatter.formatToParts(new Date());
    const get = type => parseInt(parts.find(p => p.type === type)?.value, 10);

    return new Date(
      get('year'),
      get('month') - 1, // JavaScript months are 0-indexed
      get('day'),
      get('hour'),
      get('minute'),
      get('second')
    );
  }

  // Baden-Württemberg holidays + company no-shipping days 2025-2035
  const HOLIDAYS_BW = {
    // Fixed holidays and no-shipping days
    fixed: [
      '01-01', // Neujahr
      '01-06', // Heilige Drei Könige
      '05-01', // Tag der Arbeit
      '10-03', // Tag der Deutschen Einheit
      '11-01', // Allerheiligen
      '12-24', // Heiligabend (no shipping)
      '12-25', // 1. Weihnachtstag
      '12-26', // 2. Weihnachtstag / Santo Stefano
      '12-30', // No shipping
      '12-31', // Silvester (no shipping)
    ],
    // Variable holidays (Easter-based) - Karfreitag, Ostersonntag, Ostermontag, Christi Himmelfahrt, Pfingstmontag, Fronleichnam
    variable: {
      2025: ['04-18', '04-20', '04-21', '05-29', '06-09', '06-19'],
      2026: ['04-03', '04-05', '04-06', '05-14', '05-25', '06-04'],
      2027: ['03-26', '03-28', '03-29', '05-06', '05-17', '05-27'],
      2028: ['04-14', '04-16', '04-17', '05-25', '06-05', '06-15'],
      2029: ['03-30', '04-01', '04-02', '05-10', '05-21', '05-31'],
      2030: ['04-19', '04-21', '04-22', '05-30', '06-10', '06-20'],
      2031: ['04-11', '04-13', '04-14', '05-22', '06-02', '06-12'],
      2032: ['03-26', '03-28', '03-29', '05-06', '05-17', '05-27'],
      2033: ['04-15', '04-17', '04-18', '05-26', '06-06', '06-16'],
      2034: ['04-07', '04-09', '04-10', '05-18', '05-29', '06-08'],
      2035: ['03-23', '03-25', '03-26', '05-03', '05-14', '05-24'],
    }
  };

  /**
   * Check if a date is a holiday
   */
  function isHoliday(date, customHolidays = []) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const monthDay = `${month}-${day}`;

    // Check custom holidays
    if (customHolidays.includes(dateStr)) {
      return true;
    }

    // Check fixed holidays
    if (HOLIDAYS_BW.fixed.includes(monthDay)) {
      return true;
    }

    // Check variable holidays for this year
    if (HOLIDAYS_BW.variable[year]) {
      return HOLIDAYS_BW.variable[year].includes(monthDay);
    }

    return false;
  }

  /**
   * Find the next business day (not a weekend or holiday)
   * Returns: { type: 'tomorrow'|'weekday', weekday?: string, daysAhead?: number, date?: Date }
   */
  function getNextBusinessDayType(customHolidays = []) {
    const now = getGermanDate();
    let checkDate = new Date(now);

    // Start checking from tomorrow
    checkDate.setDate(checkDate.getDate() + 1);

    // Check up to 14 days ahead to find next business day (handles long holiday periods)
    for (let i = 0; i < 14; i++) {
      const dayOfWeek = checkDate.getDay();

      // Skip weekends
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        checkDate.setDate(checkDate.getDate() + 1);
        continue;
      }

      // Skip holidays
      if (isHoliday(checkDate, customHolidays)) {
        checkDate.setDate(checkDate.getDate() + 1);
        continue;
      }

      // Found a business day - determine if it's "tomorrow" or a specific weekday
      // Compare dates normalized to midnight to avoid time-of-day edge cases
      const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const checkDateNormalized = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
      const daysAhead = Math.floor((checkDateNormalized - nowDate) / (1000 * 60 * 60 * 24));

      // If it's literally tomorrow (1 day away), use 'tomorrow' for better UX
      if (daysAhead === 1) {
        return { type: 'tomorrow' };
      }

      // For any other business day, return weekday name with date info for distant dates
      return {
        type: 'weekday',
        weekday: WEEKDAYS_DE[dayOfWeek],
        daysAhead,
        date: new Date(checkDate) // Clone to avoid mutation
      };
    }

    // Fallback (shouldn't reach here unless 14+ consecutive non-business days)
    console.warn('Same-day shipping: Could not find business day within 14 days');
    const dayOfWeek = checkDate.getDay();
    return {
      type: 'weekday',
      weekday: WEEKDAYS_DE[dayOfWeek],
      daysAhead: 15,
      date: new Date(checkDate)
    };
  }

  /**
   * Determine shipping status and day
   * Returns: { type: 'today'|'tomorrow'|'weekday', weekday?: string, isBeforeCutoff: boolean, daysAhead?: number, date?: Date }
   */
  function getShippingStatus(cutoffHour = 15, cutoffMinute = 59, cutoffHourFriday = 14, cutoffMinuteFriday = 59, customHolidays = []) {
    const now = getGermanDate();
    const dayOfWeek = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const hour = now.getHours();
    const minute = now.getMinutes();

    // Weekend - ships next business day
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      const nextDay = getNextBusinessDayType(customHolidays);
      return { ...nextDay, isBeforeCutoff: false };
    }

    // Holiday - ships next business day
    if (isHoliday(now, customHolidays)) {
      const nextDay = getNextBusinessDayType(customHolidays);
      return { ...nextDay, isBeforeCutoff: false };
    }

    // Friday
    if (dayOfWeek === 5) {
      const isBeforeCutoff = hour < cutoffHourFriday || (hour === cutoffHourFriday && minute < cutoffMinuteFriday);
      if (isBeforeCutoff) {
        return { type: 'today', isBeforeCutoff: true };
      }
      // After cutoff on Friday - find next business day
      const nextDay = getNextBusinessDayType(customHolidays);
      return { ...nextDay, isBeforeCutoff: false };
    }

    // Monday-Thursday
    const isBeforeCutoff = hour < cutoffHour || (hour === cutoffHour && minute < cutoffMinute);
    if (isBeforeCutoff) {
      return { type: 'today', isBeforeCutoff: true };
    }
    // After cutoff - find next business day
    const nextDay = getNextBusinessDayType(customHolidays);
    return { ...nextDay, isBeforeCutoff: false };
  }

  /**
   * Calculate time remaining until cutoff
   * Uses Friday-specific cutoff on Fridays
   */
  function getTimeUntilCutoff(cutoffHour = 15, cutoffMinute = 59, cutoffHourFriday = 14, cutoffMinuteFriday = 59) {
    const now = getGermanDate();
    const dayOfWeek = now.getDay();

    // Use Friday cutoff on Fridays
    const activeHour = dayOfWeek === 5 ? cutoffHourFriday : cutoffHour;
    const activeMinute = dayOfWeek === 5 ? cutoffMinuteFriday : cutoffMinute;

    const cutoff = getGermanDate();
    cutoff.setHours(activeHour, activeMinute, 0, 0);

    const diff = cutoff - now;

    if (diff <= 0) {
      return null;
    }

    let hours = Math.floor(diff / (1000 * 60 * 60));
    // Use ceil for minutes to give users a safety buffer (2m 30s remaining shows "3 Min.")
    let minutes = Math.ceil((diff % (1000 * 60 * 60)) / (1000 * 60));

    // Handle rollover: 59m 30s would ceil to 60 minutes, display as "1 Std." instead
    if (minutes === 60) {
      hours++;
      minutes = 0;
    }

    return { hours, minutes };
  }

  /**
   * Format shipping message
   * @param {string} shippingType - 'today', 'tomorrow', or 'weekday'
   * @param {string|null} weekday - weekday name (only for 'weekday' type)
   * @param {number|null} hours - hours remaining (only for 'today')
   * @param {number|null} minutes - minutes remaining (only for 'today')
   * @param {number|null} daysAhead - days until shipping (for 'weekday' type)
   * @param {Date|null} date - shipping date (for 'weekday' type when > 7 days)
   */
  function formatShippingMessage(shippingType, weekday = null, hours = null, minutes = null, daysAhead = null, date = null) {
    if (shippingType === 'today' && hours !== null && minutes !== null) {
      const parts = [hours > 0 ? `${hours} Std.` : null, minutes > 0 ? `${minutes} Min.` : null].filter(Boolean).join(' ');
      if (parts) {
        return `Versand heute bei Bestellung innerhalb ${parts}`;
      }
      // If no time parts, caller should have passed a different shippingType
      // Fall through to handle as 'tomorrow' or 'weekday' below
    }

    if (shippingType === 'tomorrow') {
      return 'Versand morgen';
    }

    if (shippingType === 'weekday' && weekday) {
      // If more than 7 days away, include the date for clarity (e.g., "Versand Montag, 6. Januar")
      if (daysAhead && daysAhead > 7 && date) {
        const day = date.getDate();
        const month = MONTHS_DE[date.getMonth()];
        return `Versand ${weekday}, ${day}. ${month}`;
      }
      return `Versand ${weekday}`;
    }

    return '';
  }

  /**
   * Update shipping info display
   */
  function updateCountdown() {
    const countdownElements = document.querySelectorAll('[data-same-day-countdown]');

    if (countdownElements.length === 0) {
      return;
    }

    // Get settings from Shopify
    const enableSameDayShipping = window.Shopify?.theme?.settings?.enable_same_day_shipping !== false;

    if (!enableSameDayShipping) {
      countdownElements.forEach(el => el.style.display = 'none');
      return;
    }

    // Mon-Thu cutoff time (default: 15:59)
    const cutoffTime = window.Shopify?.theme?.settings?.same_day_cutoff_time || '15:59';
    const [h, m] = cutoffTime.split(':').map(Number);
    const cutoffHour = isNaN(h) ? 15 : h;
    const cutoffMinute = isNaN(m) ? 59 : m;

    // Friday cutoff time (default: 14:59)
    const cutoffTimeFriday = window.Shopify?.theme?.settings?.same_day_cutoff_time_friday || '14:59';
    const [hFri, mFri] = cutoffTimeFriday.split(':').map(Number);
    const cutoffHourFriday = isNaN(hFri) ? 14 : hFri;
    const cutoffMinuteFriday = isNaN(mFri) ? 59 : mFri;

    const customHolidaysStr = window.Shopify?.theme?.settings?.custom_holidays || '';
    const customHolidays = customHolidaysStr
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    // Get shipping status
    const status = getShippingStatus(cutoffHour, cutoffMinute, cutoffHourFriday, cutoffMinuteFriday, customHolidays);

    // Get time remaining if shipping today
    let shippingMessage;
    if (status.type === 'today' && status.isBeforeCutoff) {
      const time = getTimeUntilCutoff(cutoffHour, cutoffMinute, cutoffHourFriday, cutoffMinuteFriday);
      // Check for valid time with at least some minutes remaining
      if (time && (time.hours > 0 || time.minutes > 0)) {
        shippingMessage = formatShippingMessage('today', null, time.hours, time.minutes);
      } else {
        // Cutoff passed or no time remaining - find next business day
        const nextDay = getNextBusinessDayType(customHolidays);
        shippingMessage = formatShippingMessage(nextDay.type, nextDay.weekday, null, null, nextDay.daysAhead, nextDay.date);
      }
    } else {
      shippingMessage = formatShippingMessage(status.type, status.weekday, null, null, status.daysAhead, status.date);
    }

    countdownElements.forEach(el => {
      // Only show countdown when the order ships today, i.e. requested quantity ≤ stock.
      // Backorders (qty > stock for continue-policy items) ship in 5-7 days, not today.
      const deliveryInfo = el.closest('[data-delivery-info]');
      if (deliveryInfo) {
        const stock = parseInt(deliveryInfo.getAttribute('data-stock'), 10) || 0;
        const available = deliveryInfo.getAttribute('data-available') === 'true';
        const quantity = parseInt(deliveryInfo.getAttribute('data-quantity'), 10) || 1;

        if (!available || stock <= 0 || quantity > stock) {
          el.style.display = 'none';
          return;
        }
      }

      el.textContent = shippingMessage;
      el.style.display = '';
    });
  }

  // Initialize countdown
  function init() {
    updateCountdown();
    // Update every minute
    setInterval(updateCountdown, 60000);
  }

  // Start on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API — delivery-info.js calls this after variant/quantity changes so the
  // countdown text stays visible (SSR innerHTML swap blanks it out).
  window.craftDepotUpdateSameDayShipping = updateCountdown;
})();
